<template>
  <Modal 
    ref="conditionsModalRef"
    @onNegativeActionClick="closeModal"
    class="conditions-modal"
    :showIcon="false"
    :withCloser="true"
    :modalStyle="{ width: '908px', padding: '20px' }"
  >
    <template v-slot:body>
      <div class="conditions-modal-content">
        <div class="section-top">
          <h2 class="main-title">Erken ödeme koşullarını nasıl sağlarım?</h2>
          <p class="description">Aşağıdaki koşullar sağlandığında ve aktif bir erken ödeme talebinizin bulunmaması durumunda erken ödeme talebi oluşturabilirsiniz.</p>
          
          <div class="condition-boxes">
            <div class="condition-box" :class="[scoreStatusClass, scoreBackgroundClass]">
              <div class="box-header">
                <Icons name="StoreBadge" size="lg" color="#1C1C1E" />
                <span class="box-title">Mağaza puanınız {{ props.sellerGradeLimit }} ve üzeri olmalı</span>
              </div>
              <div class="box-content">
                <div class="label">
                  <span class="label-text">Mağaza Puanınız:</span>
                  <div v-if="props.sellerGrade == null" class="value-container value-null">
                    <Icons name="NullDash" class="null-icon" color="white" />
                  </div>
                  <div v-else class="value-container" :class="scoreValueClass">
                    <span class="value-text">{{ props.sellerGrade }}</span>
                  </div>
                </div>
                <div class="info">
                  <span class="info-text">Mağazanın son 3 aydaki yönetim performansı ve müşteri memnuniyeti verilerine göre hesaplanır.</span>
                </div>
              </div>
            </div>

            <div class="condition-box" :class="[paymentStatusClass, paymentBackgroundClass]">
              <div class="box-header">
                <Icons name="WalletPayment" size="lg" color="#1C1C1E" />
                <span class="box-title">En az {{ props.settlementCountLimit }} hak ediş ödemesi almış olmalısınız</span>
              </div>
              <div class="box-content">
                <div class="label">
                  <span class="label-text">Geçmiş hak ediş ödemesi sayınız:</span>
                  <span class="value">{{ props.settlementCount }}</span>
                </div>
                <div class="info">
                  <span class="info-text">{{ paymentInfoText }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div class="section-bottom">
          <h3 class="section-title">Mağaza puanımı nasıl yükseltirim?</h3>
          <p class="section-description">Sipariş yönetimi, müşteri iletişimi ve teslimat performansınız ile ilgili aşağıdaki adımlar, puanınızı korumanıza ve yükseltmenize yardımcı olur.</p>
          
          <div class="info-cards">
            <div class="info-card">
              <div class="icon-title">
                <Icons name="Help" class="card-icon-40" color="#1C1C1E" />
                <h4>Müşteri sorularını en geç <br> 24 saat içinde yanıtlayın</h4>
              </div>
              <p>Hızlı geri dönüşler, müşteri memnuniyetini artırır ve iletişim performansınıza olumlu yansır.</p>
            </div>
            
            <div class="info-card">
              <div class="icon-title">
                <Icons name="CloseCircleLg" class="card-icon-40" color="#1C1C1E" />
                <h4>Siparişleri <br> reddetmekten kaçının</h4>
              </div>
              <p>Reddedilen siparişler puanınızı doğrudan olumsuz etkiler. Stok ve teslimat planlamasını doğru yapın.</p>
            </div>
            
            <div class="info-card">
              <div class="icon-title">
                <Icons name="TimerCircle" class="card-icon-40" color="#1C1C1E" />
                <h4>Siparişleri <br> zamanında kargolayın</h4>
              </div>
              <p>Taahhüt edilen süre içinde kargolanan siparişler, teslimat performansınızı güçlendirir.</p>
            </div>
            
            <div class="info-card">
              <div class="icon-title">
                <Icons name="AlarmClock" class="card-icon-40" color="#1C1C1E" />
                <h4>Siparişlerin geciken kargolamaya düşmemesine özen gösterin</h4>
              </div>
              <p>Gecikmeler, hem müşteri memnuniyetini düşürür hem de puan kaybına neden olur.</p>
            </div>
            
            <div class="info-card">
              <div class="icon-title">
                <Icons name="PackageBox" class="card-icon-40" color="#1C1C1E" />
                <h4>Ürünlerin hasarsız ve eksiksiz gönderimini sağlayın</h4>
              </div>
              <p>Doğru paketleme sayesinde iade talepleri azalır, satış sonrası performansınız yükselir.</p>
            </div>
          </div>
        </div>
      </div>
    </template>
  </Modal>
</template>

<script>
import { ref, computed } from 'vue';
import { Modal, Icons } from '@n11/so-ui-components';

export default {
  name: "ConditionsModal",
  components: {
    Modal,
    Icons,
  },
  props: {
    sellerGrade: {
      type: Number
    },
    settlementCount: {
      type: Number
    },
    sellerGradeLimit: {
      type: Number
    },
    settlementCountLimit: {
      type: Number
    }
  },
  setup(props) {
    const conditionsModalRef = ref(null);
    
    const openModal = () => {
      if (conditionsModalRef.value) {
        conditionsModalRef.value.openModal();
      }
    };
    
    const closeModal = () => {
      if (conditionsModalRef.value) {
        conditionsModalRef.value.closeModal();
      }
    };

    const scoreBackgroundClass = computed(() => {
      if (props.sellerGrade == null) return 'bg-null';
      if (props.sellerGrade >= props.sellerGradeLimit) return 'bg-success';
      if (props.sellerGrade >= 5) return 'bg-warning';
      if (props.sellerGrade >= 3) return 'bg-orange';
      return 'bg-error';
    });

    const scoreStatusClass = computed(() => {
      if (props.sellerGrade == null) return 'status-null';
      if (props.sellerGrade >= props.sellerGradeLimit) return 'status-success';
      if (props.sellerGrade >= 5) return 'status-warning';
      if (props.sellerGrade >= 3) return 'status-orange';
      return 'status-error';
    });

    const scoreValue = computed(() => {
      if (props.sellerGrade == null) return '—';
      return props.sellerGrade;
    });

    const scoreValueClass = computed(() => {
      if (props.sellerGrade == null) return 'highlight-null';
      if (props.sellerGrade >= props.sellerGradeLimit) return 'highlight-success';
      if (props.sellerGrade >= 5) return 'highlight-warning';
      if (props.sellerGrade >= 3) return 'highlight-orange';
      return 'highlight-error';
    });

    const paymentBackgroundClass = computed(() => {
      if (props.settlementCount == null) return 'bg-null';
      if (props.settlementCount >= props.settlementCountLimit) return 'bg-success';
      return 'bg-error';
    });

    const paymentStatusClass = computed(() => {
      if (props.settlementCount == null) return 'status-null';
      if (props.settlementCount >= props.settlementCountLimit) return 'status-success';
      return 'status-error';
    });

    const paymentInfoText = computed(() => {
      if (props.settlementCount == null) {
        return 'Hak ediş ödemesi bilgisi bulunmamaktadır.';
      }
      
      if (props.settlementCount >= props.settlementCountLimit) {
        return 'Erken ödeme talep etmek için yeterli hak ediş ödemesi aldınız.';
      }
      
      const remaining = props.settlementCountLimit - props.settlementCount;
      return `Erken ödeme talep edebilmeniz için ${remaining} hak ediş daha kaldı.`;
    });
    
    return {
      conditionsModalRef,
      openModal,
      closeModal,
      scoreBackgroundClass,
      scoreStatusClass,
      scoreValue,
      scoreValueClass,
      paymentBackgroundClass,
      paymentStatusClass,
      paymentInfoText,
      props,
    };
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/styles/variables.scss';

.conditions-modal {
  :deep(.modal-body) {
    padding: 0 !important;
    text-align: left !important;
    width: 100% !important;
    max-height: 80vh !important;
    overflow-y: auto !important;

    .content {
      text-align: left !important;
      align-items: flex-start !important;
      width: 100% !important;
    }
  }

  :deep(.modal-footer) {
    display: none !important;
  }
}

.conditions-modal-content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
  width: 100%;
}

.section-top,
.section-bottom {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;

  .description,
  .section-description {
    align-self: stretch;
    color: $colorN80;
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    letter-spacing: -0.05px;
    margin: 0;
  }
}

.section-top {
  padding-bottom: 16px;
  border-bottom: 0.5px solid $colorN40;

  .main-title {
    align-self: stretch;
    color: $colorN80;
    font-size: 20px;
    font-weight: 700;
    line-height: 26px;
    letter-spacing: -0.2px;
    margin-top: 24px;
  }
}

.section-bottom {
  .section-title {
    color: $colorN80;
    font-size: 16px;
    font-weight: 600;
    line-height: 20px;
    letter-spacing: -0.05px;
    margin: 0;
  }
}

.condition-boxes {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  align-self: stretch;
  gap: 16px;
}

.condition-box {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
  padding: 12px;
  border-radius: 6px;
  flex: 1;
  min-height: 110px;

  &.bg-null {
    background: $colorN10;
  }

  &.bg-success {
    background: $colorS05;
  }

  &.bg-warning {
    background: $colorY05;
  }

  &.bg-orange {
    background: #FFF0E6;
  }

  &.bg-error {
    background: $colorE05;
  }

  .box-header {
    display: flex;
    align-items: center;
    gap: 4px;

    .box-title {
      color: $colorN80;
      font-size: 16px;
      font-weight: 600;
      line-height: 20px;
      letter-spacing: -0.05px;
    }
  }

  .box-content {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
    align-self: stretch;

    .label-text,
    .info-text {
      color: $colorN80;
      font-size: 14px;
      font-weight: 400;
      line-height: 18px;
      letter-spacing: -0.05px;
    }

    .value {
      color: $colorN80;
      font-size: 14px;
      font-weight: 600;
      line-height: 18px;
      letter-spacing: -0.05px;
    }

    .label {
      display: flex;
      padding: 0 28px;
      align-items: center;
      gap: 4px;
      align-self: stretch;
    }

    .info {
      display: flex;
      padding-left: 28px;
      align-items: center;
      gap: 4px;
      align-self: stretch;
    }

    .value-container {
      display: flex;
      padding: 2px 4px;
      align-items: flex-start;
      gap: 8px;
      border-radius: 4px;

      .value-text {
        color: $colorN00;
        font-size: 11px;
        font-weight: 700;
        line-height: 12px;
      }

      &.value-null {
        width: 24px;
        height: 16px;
        padding: 2px 6px;
        justify-content: center;
        align-items: center;
        background: $colorS60;

        .null-icon {
          :deep(svg) {
            width: 7.3px;
            height: 1.7px;
            fill: $colorN00;
          }
        }
      }

      &.highlight-success {
        background: $colorS50;
      }

      &.highlight-warning {
        background: $colorY40;
      }

      &.highlight-orange {
        background: #FF8B3D;
      }

      &.highlight-error {
        background: $colorE50;
      }

      &.highlight-null {
        background: $colorS60;
      }
    }
  }
}

.info-cards {
  display: flex;
  align-items: flex-start;
  align-content: flex-start;
  gap: 16px;
  align-self: stretch;
  border-radius: 6px;
  flex-wrap: wrap;

  .info-card {
    display: flex;
    flex: 0 0 calc(33.33% - 11px);
    height: 192px;
    padding: 24px;
    flex-direction: column;
    justify-content: space-between;
    align-items: center;
    border-radius: 8px;
    border: 1px solid $colorN40;
    background: $colorN00;

    .icon-title {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      gap: 12px;

      .card-icon-40 {
        :deep(svg) {
          width: 40px;
          height: 40px;
        }
      }

      h4 {
        color: $colorN80;
        text-align: center;
        font-size: 14px;
        font-weight: 600;
        line-height: 18px;
        letter-spacing: -0.05px;
        margin: 0;
      }
    }

    p {
      align-self: stretch;
      color: $colorN70;
      text-align: center;
      font-size: 12px;
      font-weight: 400;
      line-height: 16px;
      margin: 0;
    }
  }
}
</style>
